/**
 * Pump Command Center — dashboard home with health summary and module launcher.
 */
import { PUMP_LAUNCHER_MODULES } from './adminPumpHubConfig';
import { healthTone } from './AdminPumpHubHeader';
import { formatIndianNumber } from '../../../utils/reportFormat';
import AdminPumpHealthRadar from './AdminPumpHealthRadar';

export default function AdminPumpOverviewDashboard({
  analyticsSummary = {},
  analyticsSummaryLoading = false,
  lockStart,
  lockEnd,
  onLockStartChange,
  onLockEndChange,
  onSaveLockDates,
  maintenanceMode = false,
  maintenanceMessage = '',
  onMaintenanceModeChange,
  onMaintenanceMessageChange,
  onSaveMaintenance,
  onLaunchModule,
}) {
  const score = analyticsSummary.overall_health_score;
  const scoreLoaded = analyticsSummary.loaded === true && score != null;
  const tone = healthTone(scoreLoaded ? score : 0);

  return (
    <div className="admin-pump-hub__module-content">
      <div className="admin-pump-hub__section-head">
        <h2 className="admin-pump-hub__section-title">Pump summary</h2>
        <p className="admin-pump-hub__section-desc">
          Start with Owner Dashboard, then work through Business Intelligence, Data Analytics, Operations, Accounting Integrity, and Owner&apos;s Fund Analytics.
        </p>
      </div>

      <div className="admin-pump-hub__panel mb-3">
        <h3 className="h6 mb-1">Maintenance mode (this pump)</h3>
        <p className="small text-muted mb-3">
          Restrict login and data entry for this pump during migration. Super admin can still manage the pump.
          For all pumps, use Master Settings → Platform config.
        </p>
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="pump_maintenance_mode"
            checked={!!maintenanceMode}
            onChange={(e) => onMaintenanceModeChange?.(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="pump_maintenance_mode">
            Enable maintenance mode for this pump
          </label>
        </div>
        <div className="mb-3">
          <label className="form-label small mb-1">Client-facing message</label>
          <textarea
            className="form-control form-control-sm"
            rows={2}
            value={maintenanceMessage || ''}
            onChange={(e) => onMaintenanceMessageChange?.(e.target.value)}
            placeholder="This pump is under maintenance. Please try again later."
          />
        </div>
        <button type="button" className="btn btn-warning btn-sm" onClick={onSaveMaintenance}>
          Save maintenance settings
        </button>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-lg-4">
          <div className="admin-pump-hub__panel admin-pump-hub__health-card h-100">
            <div className={`admin-pump-hub__health-ring admin-pump-hub__health-ring--${tone}`}>
              <span className="admin-pump-hub__health-score">{scoreLoaded ? formatIndianNumber(score, 2) : '—'}</span>
              <span className="admin-pump-hub__health-label">Health</span>
            </div>
            <strong>
              {analyticsSummaryLoading
                ? 'Calculating…'
                : scoreLoaded
                  ? (analyticsSummary.health_label || 'Business health')
                  : 'Not loaded yet'}
            </strong>
            <p className="small text-muted mt-2 mb-0">
              {analyticsSummaryLoading
                ? 'Loading business health for the selected period…'
                : scoreLoaded
                  ? (analyticsSummary.verdict || 'Change the date range above to refresh this score.')
                  : 'Choose a report period above to calculate business health.'}
            </p>
            <AdminPumpHealthRadar
              radar={analyticsSummary.radar}
              loading={analyticsSummaryLoading}
            />
          </div>
        </div>
        <div className="col-lg-8">
          <div className="admin-pump-hub__panel h-100">
            <h3 className="h6 mb-1">What would you like to do?</h3>
            <p className="small text-muted mb-3">
              Pick a module below. Each section includes charts, plain-language insights, and PDF export.
            </p>
            <div className="admin-pump-hub__launcher-grid">
              {PUMP_LAUNCHER_MODULES.map((mod) => (
                <button
                  key={mod.key}
                  type="button"
                  className={`admin-pump-hub__launcher-card admin-pump-hub__launcher-card--${mod.tone}`}
                  onClick={() => onLaunchModule(mod.module, mod.reportView)}
                >
                  <span className="admin-pump-hub__launcher-icon" aria-hidden>{mod.icon}</span>
                  <span className="admin-pump-hub__launcher-title">{mod.title}</span>
                  <p className="admin-pump-hub__launcher-desc">{mod.description}</p>
                  <span className="admin-pump-hub__launcher-arrow">Open module →</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="admin-pump-hub__panel">
        <h3 className="h6 mb-1">Accounting lock dates</h3>
        <p className="small text-muted mb-3">
          Restrict how far back users can edit accounts for this pump. Super admin only.
        </p>
        <div className="admin-pump-hub__settings-grid">
          <div>
            <label className="form-label small mb-1">Lock start date</label>
            <input type="date" className="form-control form-control-sm" value={lockStart} onChange={(e) => onLockStartChange(e.target.value)} />
          </div>
          <div>
            <label className="form-label small mb-1">Lock end date</label>
            <input type="date" className="form-control form-control-sm" value={lockEnd} onChange={(e) => onLockEndChange(e.target.value)} />
          </div>
          <div>
            <button type="button" className="btn btn-primary btn-sm" onClick={onSaveLockDates}>
              Save lock dates
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
